import { useCallback, type ReactNode } from "react";
import { useTerminalClasses } from "../theme/context";
import { cx } from "../util/cx";
import type { TerminalController } from "../hooks/useTerminalController";
import { MessageList } from "./MessageList";
import { InputBar } from "./InputBar";
import type { TerminalMessage } from "../types";

export interface TerminalPanelProps {
  controller: TerminalController;
  /**
   * Docked: the panel fills the host's container instead of standing at its own height, so
   * `controller.panelHeight` and the drag-to-resize top edge are both dropped — sizing is the
   * host layout's job now.
   */
  docked?: boolean;
  header?: (controller: TerminalController) => ReactNode;
  renderMessage?: (message: TerminalMessage) => ReactNode;
  empty?: () => ReactNode;
}

const MIN_HEIGHT = 160;
const MAX_HEIGHT_RATIO = 0.9;

export function TerminalPanel({
  controller,
  docked = false,
  header,
  renderMessage,
  empty,
}: TerminalPanelProps) {
  const classes = useTerminalClasses();

  // Drag the top edge to resize. Height grows as the pointer moves up.
  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startY = e.clientY;
      const startHeight = controller.panelHeight;
      const maxHeight =
        (typeof window !== "undefined" ? window.innerHeight : 800) *
        MAX_HEIGHT_RATIO;

      const onMove = (ev: PointerEvent) => {
        const next = Math.min(
          Math.max(startHeight + (startY - ev.clientY), MIN_HEIGHT),
          maxHeight,
        );
        controller.setPanelHeight(next);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [controller],
  );

  return (
    <div
      className={cx(classes.panel, docked && "rt-panel--docked")}
      style={docked ? undefined : { height: controller.panelHeight }}
    >
      {!docked && (
        <div
          className={classes.resizeHandle}
          onPointerDown={onResizeStart}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize terminal"
        />
      )}

      {header ? (
        header(controller)
      ) : (
        <div className={classes.panelHeader}>
          <span className="rt-panel-title">{controller.title}</span>
          <div className="rt-panel-header-spacer" />
          {controller.providers.length > 1 && (
            <select
              className={classes.providerSwitch}
              value={controller.activeProviderId}
              onChange={(e) => controller.setActiveProvider(e.target.value)}
              aria-label="Active provider"
            >
              {controller.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} · {p.model}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className="rt-icon-button"
            onClick={controller.clearMessages}
            title="Clear conversation"
            aria-label="Clear conversation"
          >
            ⌫
          </button>
          <button
            type="button"
            className="rt-icon-button"
            onClick={controller.close}
            title="Collapse"
            aria-label="Collapse terminal"
          >
            ▾
          </button>
        </div>
      )}

      <MessageList
        messages={controller.messages}
        renderMessage={renderMessage}
        empty={empty}
      />

      <InputBar
        placeholder={controller.placeholder}
        busy={controller.busy}
        onSubmit={controller.submit}
        onAbort={controller.abort}
        autoFocus
      />
    </div>
  );
}
